import { motion } from "framer-motion";
import { useLang } from "@/i18n";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function Story() {
  const { t } = useLang();
  const chapters = [
    { n: "01", title: t("story.c1t"), desc: t("story.c1d") },
    { n: "02", title: t("story.c2t"), desc: t("story.c2d") },
    { n: "03", title: t("story.c3t"), desc: t("story.c3d") },
  ];

  return (
    <section className="py-24 md:py-32" data-testid="story-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <motion.p {...fadeUp} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-terracotta" />
              {t("story.eyebrow")}
            </motion.p>
            <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-5 font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main" data-testid="story-title">
              {t("story.title")}
            </motion.h2>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-8 text-base md:text-lg leading-relaxed text-brown font-light max-w-2xl">
              {t("story.p1")}
            </motion.p>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="mt-5 text-base md:text-lg leading-relaxed text-brown font-light max-w-2xl">
              {t("story.p2")}
            </motion.p>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.4 }} className="mt-8 font-serif italic text-2xl md:text-3xl text-terracotta" data-testid="story-signature">
              „{t("story.sig")}"
            </motion.p>

            <div className="mt-14 space-y-0">
              {chapters.map((c, i) => (
                <motion.div
                  key={c.n}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.12 }}
                  className="flex gap-6 md:gap-10 py-7 border-t border-warm-grey/50 last:border-b"
                  data-testid={`story-chapter-${c.n}`}
                >
                  <span className="font-serif italic text-3xl md:text-4xl text-warm-grey shrink-0 w-14">{c.n}</span>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl text-main">{c.title}</h3>
                    <p className="mt-2 text-sm md:text-base text-brown/80 font-light leading-relaxed max-w-lg">{c.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:sticky lg:top-28"
            >
              <div className="overflow-hidden rounded-3xl aspect-[3/4] group">
                <img
                  src="/assets/bedroom.jpg"
                  alt="Cameră luminoasă cu pat king-size la Imperfect House"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  data-testid="story-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 md:-left-8 bg-green-dark text-cream-light rounded-2xl px-6 py-5 shadow-xl max-w-[240px]">
                <p className="font-serif italic text-lg leading-snug">„Chiar ne-am simțit de parcă am mers în propria casă de vacanță."</p>
                <p className="mt-2 text-xs tracking-widest uppercase opacity-70">Oana · 10/10</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
